import { PUBLIC_TOOLS, absoluteUrl, type PublicTool } from "@/lib/tools-catalog";

export type JsonLdValue = string | number | boolean | null | JsonLdValue[] | { [key: string]: JsonLdValue };
export type JsonLdObject = { [key: string]: JsonLdValue };

export function buildToolSoftwareApplicationJsonLd(tool: PublicTool): JsonLdObject {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: tool.name,
    applicationCategory: "UtilitiesApplication",
    operatingSystem: "Web",
    url: absoluteUrl(tool.path),
    description: tool.description,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "ZAR",
    },
  };
}

export function buildToolsCollectionJsonLd(): JsonLdObject {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Free Tools",
    url: absoluteUrl("/tools"),
    description:
      "Practical, no-signup tools for South African students and small businesses: AI Humaniser, Paraphraser, OCR Scanner and a browser-based PDF toolkit.",
    hasPart: PUBLIC_TOOLS.map((tool) => buildToolSoftwareApplicationJsonLd(tool)),
  };
}

function isNonEmptyString(value: unknown) {
  return typeof value === "string" && value.trim().length > 0;
}

export function validateStructuredData(schema: JsonLdObject) {
  const errors: string[] = [];
  if (schema["@context"] !== "https://schema.org") errors.push("@context must be https://schema.org");
  if (!isNonEmptyString(schema["@type"])) errors.push("@type is required");
  if (!isNonEmptyString(schema.name)) errors.push("name is required");
  if (!isNonEmptyString(schema.url)) errors.push("url is required");
  if (!isNonEmptyString(schema.description)) errors.push("description is required");

  if (schema["@type"] === "SoftwareApplication") {
    if (!isNonEmptyString(schema.applicationCategory)) {
      errors.push("SoftwareApplication.applicationCategory is required");
    }
    if (!isNonEmptyString(schema.operatingSystem)) {
      errors.push("SoftwareApplication.operatingSystem is required");
    }
    const offer = schema.offers as JsonLdObject | undefined;
    if (!offer || offer["@type"] !== "Offer") errors.push("SoftwareApplication.offers must be an Offer");
    if (offer && !isNonEmptyString(offer.price)) errors.push("Offer.price is required");
    if (offer && !isNonEmptyString(offer.priceCurrency)) errors.push("Offer.priceCurrency is required");
  }

  if (schema["@type"] === "CollectionPage") {
    const parts = schema.hasPart;
    if (!Array.isArray(parts) || parts.length === 0) {
      errors.push("CollectionPage.hasPart must list the tools");
    } else {
      parts.forEach((part, index) => {
        const childErrors = validateStructuredData(part as JsonLdObject);
        childErrors.errors.forEach((error) => errors.push(`hasPart[${index}]: ${error}`));
      });
    }
  }

  return { valid: errors.length === 0, errors };
}

export function getToolStructuredDataStatus() {
  return PUBLIC_TOOLS.map((tool) => {
    const schema = buildToolSoftwareApplicationJsonLd(tool);
    return {
      id: tool.id,
      name: tool.name,
      path: tool.path,
      schema,
      ...validateStructuredData(schema),
    };
  });
}